import React, { useState } from 'react';
import styled from 'styled-components';
import { IconProps } from '../../../assets/icon/icon';

interface IconButtonProps {
  /**
   * The icon component to render
   */
  icon: React.ComponentType<IconProps>;
  /**
   * Default fill color of the icon
   */
  defaultFill?: string;
  /**
   * Color of the icon when hovered
   */
  hoverFill?: string;
  /**
   * Size of the icon
   */
  size?: number;
  /**
   * Click handler
   */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Additional CSS class
   */
  className?: string;
  /**
   * Disable the button
   */
  disabled?: boolean;
  /**
   * Aria label for accessibility
   */
  ariaLabel?: string;
}

const StyledButton = styled.button<{ disabled?: boolean }>`
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? '0.5' : '1')};
  transition: all 0.2s ease;
  border-radius: 4px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }
`;

/**
 * A button that displays an icon with proper hover state handling
 */
export const IconButton: React.FC<IconButtonProps> = ({
  icon: Icon,
  defaultFill = '#656565',
  hoverFill = '#1B1A1C',
  size = 20,
  onClick,
  className,
  disabled = false,
  ariaLabel,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    if (!disabled) setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const currentFill = isHovered && !disabled ? hoverFill : defaultFill;

  return (
    <StyledButton
      className={className}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-label={ariaLabel}
    >
      <Icon fill={currentFill} size={size} />
    </StyledButton>
  );
};

export default IconButton;
